import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TablePagination, Typography } from '@material-ui/core';
import moment from 'moment';

import OverflowTooltip from '../../UI/OverflowTooltip/OverflowTooltip';
import Spinner from '../../UI/Spinner/Spinner';
import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import { clienteComprobante, tipoComprobante, texto } from '../textosComprobante';
import { claveComprobante } from '../hooks/useDatosComprobante';
import colores from '../Bandeja/coloresBandeja';

const useStyles = makeStyles(() => ({
  lista: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra
  },
  item: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '2px 12px',
    padding: '12px',
    borderBottom: `1px solid ${colores.linea}`
  },
  cliente: {
    fontWeight: 500
  },
  total: {
    textAlign: 'right',
    fontWeight: 500,
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },
  sub: {
    fontSize: '12px',
    color: colores.tenue,
    gridColumn: '1 / -1'
  },
  pie: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '4px 12px',
    marginTop: '4px',
    fontSize: 'small'
  },
  acciones: {
    whiteSpace: 'nowrap',
    marginLeft: 'auto'
  },
  vacio: {
    padding: '40px',
    textAlign: 'center',
    color: colores.tenue
  },
  paginacion: {
    '& .MuiTablePagination-toolbar': {
      paddingLeft: '8px'
    }
  }
}));

const FILAS_POR_PAGINA = [20, 50, 100];

/**
 * Los comprobantes de ComprobantesImpresionTable como tarjetas, para celular: cliente, total, comprobante, número,
 * fecha, estado (renderEstado) y acciones (renderAcciones), con la misma paginación de la tabla.
 */
const ComprobantesImpresionCards = ({ comprobantes, loading, renderEstado, renderAcciones }) => {
  const classes = useStyles();
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(FILAS_POR_PAGINA[0]);

  // Con una búsqueda nueva se vuelve a la primera página.
  useEffect(() => setPagina(0), [comprobantes]);

  if (loading) {
    return (
      <div className={classes.lista}>
        <Spinner size="small" />
      </div>
    );
  }

  if (comprobantes.length === 0) {
    return (
      <div className={`${classes.lista} ${classes.vacio}`}>
        <Typography variant="body2">No hay datos para mostrar</Typography>
      </div>
    );
  }

  const visibles = comprobantes.slice(pagina * filasPorPagina, (pagina + 1) * filasPorPagina);

  return (
    <div className={classes.lista}>
      {visibles.map((comprobante) => (
        <div key={claveComprobante(comprobante)} className={classes.item}>
          <OverflowTooltip className={classes.cliente}>{clienteComprobante(comprobante)}</OverflowTooltip>
          <div className={classes.total}>{formatNumberWithMonedaSigno(comprobante.total)}</div>
          <OverflowTooltip className={classes.sub}>
            {`${tipoComprobante(comprobante)} · ${texto(comprobante.nroExterno) || `Ref. ${comprobante.nroReferencia}`} · ${moment(comprobante.fecha).format('DD/MM/YYYY')}`}
          </OverflowTooltip>
          <div className={classes.pie}>
            <div>{renderEstado(comprobante)}</div>
            <div className={classes.acciones}>{renderAcciones(comprobante)}</div>
          </div>
        </div>
      ))}
      <TablePagination
        className={classes.paginacion}
        component="div"
        count={comprobantes.length}
        page={pagina}
        rowsPerPage={filasPorPagina}
        rowsPerPageOptions={FILAS_POR_PAGINA}
        onPageChange={(event, nueva) => setPagina(nueva)}
        onRowsPerPageChange={(event) => {
          setFilasPorPagina(parseInt(event.target.value, 10));
          setPagina(0);
        }}
        labelRowsPerPage="Filas"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        backIconButtonText="Página anterior"
        nextIconButtonText="Página siguiente"
      />
    </div>
  );
};

export default ComprobantesImpresionCards;
